import {
  action,
  computed,
  observable,
  runInAction,
  extendObservable,
  get,
  set,
  reaction,
  flow
} from 'mobx';

import { data as eventLists } from '../__mockData__/eventLists.json';
import { data as events } from '../__mockData__/events.json';
import { getEventListForObservation, getEventDetails } from '../apis/EventsApi';
import moment from 'moment';
import axios from 'axios';

const CancelToken = axios.CancelToken;
const lastSaturday = moment()
  .day(-1)
  .format('YYYY-MM-DD');
const periodCount = 4;

/**
 * Extra special care is taken to perform two important tasks:
 *  1) Cache events and event detail data so extra API requests are not made.
 *  2) Keep eventsByObservationName, eventDetails, and eventVideo properly observable and observed.
 *     Because these are observable maps with properties not known at creation time, mobx.get() and extendObservable are
 *     used to track and make mutations. Please use the built in getters and get functions instead of accessing these maps
 *     directly to make sure you get all changes.
 */
export default class EventsStore {
  cancelTokens = {
    eventsList: undefined,
    eventDetails: undefined
  };

  @observable notifications = [];

  /**
   * Map of observation names to event high level info.
   */
  @observable eventsByObservationName = {};

  /**
   * Map of event numbers to event details.
   */
  @observable eventDetails = {};

  /**
   * Map of event numbers to event video metadata.
   */
  @observable eventVideo = {};

  @observable selectedEventNumber = undefined;

  @observable
  loading = {
    eventsList: false,
    eventDetails: false
  };

  @observable
  loadingErrors = {
    eventsList: false,
    eventDetails: false
  };

  tabKeys = {
    details: 'detailsTab',
    video: 'videoTab',
    maps: 'mapsTab'
  };

  @observable activeTab = this.tabKeys.details;

  activeVideoControls = {
    pause: () => {},
    play: () => {}
  };

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  async init() {
    this.setupLogoutReaction();
  }

  /* istanbul ignore next */
  setupLogoutReaction() {
    this.clearWhenLoggedOut = reaction(
      () => this.rootStore.authStore.isLoggedIn,
      isLoggedIn => {
        if (!isLoggedIn) {
          this.reset();
        }
      },
      {
        fireImmediately: false
      }
    );
  }

  loadEventsForObservation = flow(function*(observationName, force = false) {
    if (!force && this.eventListLoadedForObservation(observationName)) {
      return;
    }

    const useMockData = yield this.shouldUseMockData();
    /* istanbul ignore if  */
    if (useMockData) {
      return this.loadMockEventList(observationName);
    }

    try {
      this.loading.eventsList = true;
      const username = this.rootStore.authStore.getUserName();
      const graphQlHubServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.graphQlHubServiceUrl
      ];

      this.cancelTokens.eventsList = CancelToken.source();
      const safetyEventList = yield getEventListForObservation(
        graphQlHubServiceUrl,
        username,
        observationName,
        lastSaturday,
        periodCount,
        this.cancelTokens.eventsList.token
      );

      const eventList = safetyEventList.filter(
        this.filterEventWithPositiveSafetyScore
      );

      if (this.eventsByObservationName[observationName]) {
        set(this.eventsByObservationName, observationName, eventList);
      } else {
        extendObservable(this.eventsByObservationName, {
          [observationName]: eventList
        });
      }

      this.loadingErrors.eventsList = false;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.loadingErrors.eventsList = true;
      }
    } finally {
      this.loading.eventsList = false;
      this.cancelTokens.eventsList = undefined;
    }
    return;
  });

  cancelLoadEventsForObservation() {
    if (this.cancelTokens.eventsList) {
      this.cancelTokens.eventsList.cancel('Request cancelled.');
    }
  }

  loadEventDetails = flow(function*(eventNumbers = [], force = false) {
    eventNumbers = eventNumbers.map(eventNumber => eventNumber.toUpperCase());
    if (!force) {
      eventNumbers = eventNumbers.filter(
        eventNumber => !this.eventDetailsLoadedForEvent(eventNumber)
      );

      if (!eventNumbers.length) {
        return;
      }
    }

    const useMockData = yield this.shouldUseMockData();
    /* istanbul ignore if  */
    if (useMockData) {
      return this.loadMockEventDetails(eventNumbers);
    }

    try {
      this.loading.eventDetails = true;
      this.cancelTokens.eventDetails = CancelToken.source();

      const graphQlHubServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.graphQlHubServiceUrl
      ];

      const safetyEventDetails = yield getEventDetails(
        graphQlHubServiceUrl,
        eventNumbers,
        this.cancelTokens.eventDetails.token
      );

      safetyEventDetails.forEach(eventDetails => {
        if (this.eventDetails[eventDetails.eventNumber]) {
          set(this.eventDetails, eventDetails.eventNumber, eventDetails);
        } else {
          extendObservable(this.eventDetails, {
            [eventDetails.eventNumber]: eventDetails
          });
        }

        this.loadingErrors.eventDetails = false;
      });
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.loadingErrors.eventDetails = true;
      }
    } finally {
      this.loading.eventDetails = false;
      this.cancelTokens.eventDetails = undefined;
    }
    return;
  });

  cancelLoadEventDetails() {
    if (this.cancelTokens.eventDetails) {
      this.cancelTokens.eventDetails.cancel('Request cancelled.');
    }
  }

  @action('Load all event details for observation')
  async loadAllEventDetailsForObservation(observationName, force = false) {
    await this.loadEventsForObservation(observationName, force);
    const eventNumbers = this.getEventsForObservationName(observationName).map(
      event => event.eventNumber
    );
    await this.loadEventDetails(eventNumbers, force);
  }

  /**
   * Mock data
   */
  @action('Load mock event list')
  /* istanbul ignore next */
  loadMockEventList(observationName, delay = 1000) {
    this.loading.eventsList = true;

    return new Promise(resolve => {
      setTimeout(() => {
        runInAction(() => {
          const eventList = eventLists
            .find(eventList => eventList.observationName === observationName)
            .events.filter(this.filterEventWithPositiveSafetyScore);

          extendObservable(this.eventsByObservationName, {
            [observationName]: eventList
          });

          this.loading.eventsList = false;
        });

        resolve(true);
      }, delay);
    });
  }

  @action('Load mock event details')
  /* istanbul ignore next */
  loadMockEventDetails(eventNumbers, delay = 1000) {
    return new Promise(resolve => {
      this.loading.eventDetails = true;

      setTimeout(() => {
        runInAction(() => {
          const eventsWithDetails = events.filter(event =>
            eventNumbers.find(eventNumber => eventNumber === event.eventNumber)
          );

          eventsWithDetails.forEach(eventDetails => {
            extendObservable(this.eventDetails, {
              [eventDetails.eventNumber]: eventDetails
            });
          });

          this.loading.eventDetails = false;
        });

        resolve(true);
      });
    });
  }

  @action('Set active tab')
  setActiveTab(tabKey) {
    this.activeTab = tabKey;
  }

  @action('Set selected event number')
  setSelectedEventNumber(eventNumber) {
    this.selectedEventNumber = eventNumber;
  }

  @action('Reset store')
  reset() {
    this.eventsByObservationName = {};
    this.eventDetails = {};
    this.loading = {
      eventsList: false,
      eventDetails: false
    };
    this.selectedEventNumber = undefined;
    this.notifications = [];
  }

  @computed
  get selectedEventDetails() {
    return get(this.eventDetails, this.selectedEventNumber) || {};
  }

  @computed
  get eventsForSelectedObservation() {
    const selectedObservationName = this.rootStore.observationsStore
      .selectedObservationName;
    if (!selectedObservationName) {
      return [];
    }
    return this.getEventsForObservationName(selectedObservationName);
  }

  @computed
  get eventDetailsForSelectedObservation() {
    const events = this.eventsForSelectedObservation;

    return events.map(event => {
      return get(this.eventDetails, event.eventNumber) || {};
    });
  }

  /**
   * Returns true if the events list has already been loaded for the given observation.
   * @param {String} observationName
   */
  eventListLoadedForObservation(observationName) {
    return !!get(this.eventsByObservationName, observationName);
  }

  /**
   * Returns true if the event details have already been loaded for given event.
   * @param {String} eventNumber
   */
  eventDetailsLoadedForEvent(eventNumber) {
    return !!get(this.eventDetails, eventNumber);
  }

  /**
   * Returns true if all event details have been loaded for the given list of event numbers.
   * @param {[String]} eventNumbers
   */
  eventDetailsLoadedForEvents(eventNumbers) {
    return eventNumbers.every(event => this.eventDetailsLoadedForEvent(event));
  }

  /**
   * Returns true if the event safety score is above 0.
   * @param event
   */
  filterEventWithPositiveSafetyScore(event) {
    return parseInt(event.safetyScore, 10) > 0;
  }

  getEventsForObservationName(observationName) {
    return get(this.eventsByObservationName, observationName) || [];
  }

  sortEventsByRecordedDate(eventList) {
    return eventList.sort((a, b) => {
      if (a.recordedOn < b.recordedOn) {
        return 1;
      } else if (a.recordedOn > b.recordedOn) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  async shouldUseMockData() {
    return this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.useMockDataInUi
    ];
  }

  /**
   * @param {String} eventNumber
   */
  eventHasVideo(eventNumber) {
    const eventWithDetails = get(this.eventDetails, eventNumber);

    return eventWithDetails && !!eventWithDetails.safetyEventVideoUrl;
  }

  pauseActiveVideo() {
    if (this.activeVideoControls.pause) {
      this.activeVideoControls.pause();
    }
  }

  playActiveVideo() {
    if (this.activeVideoControls.play) {
      this.activeVideoControls.play();
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/EventsStore.js